/**
 * Based on https://github.com/icd2k3/use-react-router-breadcrumbs
 *
 * MIT License
 *
 * Copyright (c) 2018 Justin Schrader
 * Copyright (c) 2021 Kopano b.v.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 */
import React, { useMemo } from 'react';
import type { PathMatch } from 'react-router';
import { matchPath, useLocation } from 'react-router-dom';

import type { BreadcrumbsProps } from '@mui/material';
import { Breadcrumbs as MuiBreadcrumbs, Link as MuiLink } from '@mui/material';

import { Link } from '../../components/Link';
import { useRoutesData } from '../../providers/Routes';
import type { IRouteObject } from '../../providers/Routes/Context';

type Location = ReturnType<typeof useLocation>;

export type BreadcrumbsRouteObject = IRouteObject;

export interface BreadcrumbData {
  match: PathMatch;
  location: Location;
  key: string;
  breadcrumb: React.ReactNode;
}

const render = ({
  breadcrumb: Breadcrumb,
  match,
  location,
  props,
}: {
  breadcrumb: React.ComponentType | string;
  match: PathMatch;
  location: Location;
  props?: { [x: string]: unknown };
}): BreadcrumbData => {
  const componentProps = {
    match,
    location,
    key: match.pathname,
    ...(props || {}),
  };

  return {
    ...componentProps,
    breadcrumb:
      typeof Breadcrumb === 'string' ? (
        <MuiLink
          component={Link}
          underline="hover"
          color="inherit"
          sx={{ fontWeight: match.pathname === location.pathname ? 500 : null }}
          to={match.pathname}
          key={componentProps.key}
        >
          {decodeURI(Breadcrumb)}
        </MuiLink>
      ) : (
        <Breadcrumb {...componentProps} />
      ),
  };
};

const getBreadcrumbMatch = ({
  currentSection,
  location,
  pathSection,
  routes,
}: {
  currentSection: string;
  location: Location;
  pathSection: string;
  routes: BreadcrumbsRouteObject[];
}): BreadcrumbData | null => {
  let breadcrumb: BreadcrumbData | undefined;

  routes.some(({ breadcrumb: userProvidedBreadcrumb, caseSensitive, path }) => {
    if (!path) {
      return false;
    }

    const match = matchPath(
      {
        caseSensitive,
        path,
      },
      pathSection,
    );

    if (match) {
      if (userProvidedBreadcrumb === null) {
        return true;
      }
      breadcrumb = render({
        breadcrumb: userProvidedBreadcrumb || currentSection,
        match,
        location,
      });
      return true;
    }

    return false;
  });

  if (!breadcrumb) {
    return null;
  }
  return breadcrumb;
};

export const getBreadcrumbs = ({
  routes,
  location,
}: {
  routes: BreadcrumbsRouteObject[];
  location: Location;
}): BreadcrumbData[] => {
  const matches: BreadcrumbData[] = [];
  const { pathname } = location;

  pathname
    .split('?')[0]
    // Split pathname into sections.
    .split('/')
    // Reduce over the sections and call `getBreadcrumbMatch()` for each section.
    .reduce((previousSection: string, currentSection: string, index: number) => {
      // Combine the last route section with the currentSection.
      // For example, `pathname = /1/2/3` results in match checks for
      // `/1`, `/1/2`, `/1/2/3`.
      const pathSection = !currentSection ? '/' : `${previousSection}/${currentSection}`;

      // Ignore trailing slash or double slashes in the URL
      if (pathSection === '/' && index !== 0) {
        return '';
      }

      const breadcrumb = getBreadcrumbMatch({
        currentSection,
        location,
        pathSection,
        routes,
      });
      if (breadcrumb) {
        matches.push(breadcrumb);
      }

      return pathSection === '/' ? '' : pathSection;
    });

  return matches;
};

const flattenRoutes = (routes: BreadcrumbsRouteObject[], basePath = ''): BreadcrumbsRouteObject[] =>
  routes.reduce((arr, route: BreadcrumbsRouteObject): BreadcrumbsRouteObject[] => {
    const r = {
      ...route,
      path: `${basePath}/${route.path}`,
    };
    if (r.children) {
      return arr.concat([r, ...flattenRoutes(r.children, r.path)]);
    }
    return arr.concat(r);
  }, [] as BreadcrumbsRouteObject[]);

export const useBreadcrumbs = (): BreadcrumbData[] => {
  const location = useLocation();
  const { routes } = useRoutesData();
  return useMemo(
    () =>
      getBreadcrumbs({
        routes: flattenRoutes(routes || []),
        location,
      }),
    [routes, location],
  );
};

export function Breadcrumbs(props: BreadcrumbsProps): JSX.Element {
  const breadcrumbs = useBreadcrumbs();

  return <MuiBreadcrumbs {...props}>{breadcrumbs.map(({ breadcrumb }) => breadcrumb)}</MuiBreadcrumbs>;
}

export default Breadcrumbs;
